import { LAYER_KEYS, defaultLayerOn, type LayerKey } from '@tsr/shared-types';

/**
 * DEFAULT-VIEW LAYER LOCK (#202/#236) — the lock constants live in
 * `@tsr/shared-types/layer-preferences` so BOTH sides of the contract derive
 * from one source: the web LayerPrefs defaults below AND the server's
 * `LAYER_PREFERENCE_DEFAULTS` (what GET /api/operator-preferences/layers merges
 * stored rows over). Re-exported here so web code (and the pinned
 * `defaultViewLock.test.ts`) keeps importing them from prefs.
 *
 * EXACTLY the four DEFAULT_VIEW_LAYERS render on a fresh session. No layer
 * joins the default view without explicit founder authorization = editing the
 * shared constant AND its pinned tests (web `defaultViewLock.test.ts` +
 * shared-types `layer-preferences.test.ts`). CI fails on any drift.
 */
export { DEFAULT_VIEW_LAYERS, NON_VIEW_DEFAULT_ON_LAYERS, defaultLayerOn } from '@tsr/shared-types';

/** Per-sublayer counts of response-asset features currently in view. */
export interface AssetCounts {
  fireStations: number;
  wildfireBases: number;
  aviationBases: number;
  ontarioCorps: number;
}

export const EMPTY_ASSET_COUNTS: AssetCounts = {
  fireStations: 0,
  wildfireBases: 0,
  aviationBases: 0,
  ontarioCorps: 0,
};

export interface LayerPrefs {
  cameras: boolean;
  sensors: boolean;
  smokePriorityOnly: boolean;
  // --- Response assets (R.1) ---
  /** Fire stations — background context. */
  fireStations: boolean;
  /** Provincial wildfire / air-tanker bases. */
  wildfireBases: boolean;
  /** SAR wings, air-ambulance, ISR + contractor aviation bases (Asset group:
   *  "SAR Aviation"). */
  aviationBases: boolean;
  /** Ontario Corps illustrative showcase (umbrella + partners + markers). */
  ontarioCorps: boolean;
  /** Partner names (operator_org) toggled OFF within the Ontario Corps layer. */
  hiddenPartners: string[];
  /** Ontario Corps category keys whose partner sub-list is expanded (persisted
   *  so the operator's open/closed groups survive a reload). */
  expandedCategories: string[];
  // --- Live aircraft (R.1b) ---
  /** Live ADS-B aircraft layer (Default group: "Aircraft"). L.0 layer 3. */
  liveAircraft: boolean;
  /** aircraft_role values toggled OFF within the Live Aircraft layer. */
  hiddenAircraftRoles: string[];
  // --- Wildfire layers (E.2.1) — L.1.2 two-toggle split ---
  /** Reported OUT-OF-CONTROL wildfires only (stage='OC'). L.0 layer 4, default
   *  ON. Drives the ActiveFiresLayer with a `?stage=OC` filter. */
  reportedOcWildfires: boolean;
  /** ALL reported wildfires (every stage). Weather group, default OFF,
   *  radio-exclusive. When ON it widens the ActiveFiresLayer stage filter to all
   *  stages (a superset of the OC layer). */
  allReportedWildfires: boolean;
  // --- FWI heatmap (E.2.2) — Weather group, radio-exclusive ---
  /** CWFIS Fire Weather Index heatmap (WMS imagery overlay, `public:fwi_current`).
   *  Weather group, default OFF, radio-exclusive with All Reported Wildfires. */
  fwiHeatmap: boolean;
  // --- Raw Hot Spots (E.W.0.5) — Fire Risk group ---
  /** Raw pre-fusion satellite thermal anomalies (hotspot_detections). Default OFF
   *  — `raw-hotspots` is absent from DEFAULT_VIEW_LAYERS, so `defaultLayerOn`
   *  resolves it OFF (the old "Default ON" note was stale; corrected with the
   *  Slice 2 genuineness gate, which demotes masked cells onto this + the
   *  Fusion-Candidates tier). When toggled on, RawHotspotsLayer still gates
   *  rendering to regional+ zoom (hidden at country). */
  rawHotspots: boolean;
  // --- Fusion Candidates (#370 de-flood) — Fire Risk group ---
  /** Below-urgent fused hotspots (IMS Response Level 1/2 high_risk_hotspots) the
   *  density-arm gate demoted off the Urgent Alerts surface. Default OFF;
   *  FusionCandidatesLayer gates rendering to regional+ zoom. */
  fusionCandidates: boolean;
  // --- Camera Smoke Detections (E.W.5) — Fire Risk group ---
  /** Pre-fusion camera-CV smoke detections (camera_smoke_detections, confirm-stage).
   *  Default OFF; CameraSmokeLayer gates rendering to regional+ zoom. */
  cameraSmoke: boolean;
  // --- High-risk hotspots (E.2.3) ---
  /** AI-fused high-risk hotspots (Default group: "Urgent Anomalies"). L.0 layer
   *  2, default ON (renders empty until fusion has output — fine). */
  urgentAnomalies: boolean;
  // --- Extreme Weather Warnings (EWW / L.0 Layer #2 blending) ---
  /** SUBTRACTIVE mute on the Layer #2 CAP polygon subset (Weather group,
   *  radio-EXEMPT). Default ON. Effective visibility is
   *  `urgentAnomalies && extremeWeatherWarnings`: muting this hides the CAP
   *  polygons while the AI-fused hotspots keep rendering. */
  extremeWeatherWarnings: boolean;
  // --- Evacuation Orders & Alerts (#204, Hazard group) — GeoBC order/alert area
  // polygons from the arcgis_public_features ingest. Default OFF;
  // EvacuationOrdersLayer self-gates to regional+ zoom. ---
  /** Hazard → Evacuation Orders & Alerts (arcgis_public_features, evacuation). */
  evacuationOrders: boolean;
  /** Hazard → Evacuation Orders — recorded in-COP (#445-(3), operator-declared;
   *  operator_evacuation_orders). PARALLEL to evacuationOrders. Default OFF;
   *  OperatorEvacuationOrderLayer self-gates to regional+ zoom. */
  operatorEvacuationOrders: boolean;
  // --- Public-ArcGIS category renders (#213) — the remaining ingested
  // arcgis_public_features categories surfaced via the generalized config-driven
  // renderer. All default OFF; each self-gates to regional+ zoom. ---
  /** Fire Risk → Fire Bans & Restrictions (fire_restriction). */
  fireBans: boolean;
  /** Advisories & Declarations → Drought Conditions (drought). */
  droughtConditions: boolean;
  /** Advisories & Declarations → Boil Water Advisories (public_health). */
  boilWaterAdvisories: boolean;
  /** Advisories & Declarations → State of Local Emergency (emergency_declaration). */
  stateOfLocalEmergency: boolean;
  /** Advisories & Declarations → Tropical Cyclones (hurricane). */
  tropicalCyclones: boolean;
  // --- WMS raster overlays (#212) — official government WMS rasters mounted
  // client-side via WmsOverlayLayer (no ingest, no migration). Ambient register
  // (opacity ≤0.55), both default OFF; visibility flows through MapView's
  // wmsOverlayVisibility map (keyed by layer key), not a per-layer apply ref. ---
  /** Advisories & Declarations → Hurricane Forecast Track (ECCC GeoMet WMS). */
  ecccHurricaneTrack: boolean;
  /** Flood Risk → Flood Extent (NRCan EGS Active-Floods WMS raster). */
  nrcanFloodExtent: boolean;
  // --- Drone Operator Coverage (L.2.2, Asset group) ---
  /** Drone provider coverage areas + base markers (§3B.6). Default OFF. */
  droneCoverage: boolean;
  // --- Staging Areas (R.4.2, Asset group) ---
  /** Ambient depot glyphs for open IMS staging areas (§2.5/§6.4). Default OFF. */
  stagingAreas: boolean;
  // --- Sectors (Task #64, Asset group) ---
  /** Operator-drawn sector zone-of-concern polygons (IMS §5.3; §4.3 ambient
   *  dashed boundary, never pulse). Default ON — they only exist on sectored
   *  incidents, so nothing clutters the default view. */
  sectors: boolean;
  // --- Area Command (R5 Slice 3 #507, Asset group) ---
  /** Ontario IMS §4.6 Area Command oversight markers (parent incidents,
   *  is_area_command=true) + their live child-incident count. Ambient register,
   *  STATIC. Default OFF; the layer-picker row shows the live count even while
   *  off (the PEOC first-notice cue). */
  areaCommand: boolean;
  // --- Vulnerable Communities (W.2) — hazard-agnostic community layer mounted
  // under two group-affinity toggles; both drive the SAME CommunitiesLayer
  // (MapView ORs them into `showCommunities`). Both default OFF. ---
  /** Flood Risk → Flood-Vulnerable Communities. */
  floodVulnerableCommunities: boolean;
  /** Ice Storm Risk → Vulnerable Communities. */
  iceStormVulnerableCommunities: boolean;
  /** Vulnerability-shading sub-toggle (#189, the Restrictions sub-filter pattern):
   *  when a community layer is ON, true ADDS the derived social-vulnerability band
   *  to the marker chip (text + ambient tint). Default OFF — contextual encoding,
   *  inert unless a community layer is on (which neither is by default). */
  communityVulnerabilityShading: boolean;
  // --- Power Outages (Ice Storm Risk) — first live render of the E.1.9/E.1.10
  // utility_outages ingest. Default OFF; PowerOutagesLayer self-gates rendering to
  // regional+ zoom so the country-zoom default view stays clutter-free. ---
  /** Ice Storm Risk → Power Outages (utility_outages). */
  powerOutages: boolean;
  // --- Road Closures (Ice Storm Risk) — live render of the R.1f road_closures
  // ingest (511 ON/AB + DriveBC). Default OFF; RoadClosuresLayer self-gates rendering
  // to regional+ zoom so the country-zoom default view stays clutter-free. ---
  /** Ice Storm Risk → Road Closures (road_closures). */
  roadClosures: boolean;
  /** Road Closures sub-filter (L.1 mute pattern): when Road Closures is ON, false
   *  MUTES the lane/partial restriction dots so only full closures render. Default
   *  ON (restrictions shown). */
  roadClosuresRestrictions: boolean;
  // --- Estimated Comms Degradation (Ice Storm Risk) — #109 fusion-derived ESTIMATE
  // zone (comms_degradation_zones). Default OFF; CommsDegradationZonesLayer self-gates
  // rendering to regional+ zoom so the country-zoom default view stays clutter-free. ---
  /** Ice Storm Risk → Estimated Comms Degradation (comms_degradation_zones). */
  commsDegradationZones: boolean;
  // --- Hydrometric Gauges (Flood Risk) — first live render of the E.F.1
  // hydrometric_observations ingest. Default OFF; HydrometricGaugesLayer self-gates
  // rendering to regional+ zoom so the country-zoom default view stays clutter-free. ---
  /** Flood Risk → Hydrometric Gauges (hydrometric_observations). */
  hydrometricGauges: boolean;
  // --- 3D Flood Inundation (Flood Risk) — #177 Cesium-wow. A MODELLED what-if:
  // World Terrain + OSM Buildings + a settable translucent water surface showing
  // which buildings flood. Default OFF; renders only in the bounded demo region
  // (FloodInundationLayer gates to the Kashechewan / Albany River footprint). ---
  /** Flood Risk → 3D Flood Inundation (modelled). */
  floodInundation: boolean;
  // --- Viewshed-on-select (#262, founder ruling 2026-06-11). The #178 terrain
  // line-of-sight viewshed is part of what a camera IS: selecting ANY camera
  // auto-drapes it (async compute; released on deselect). NOT a map layer — the
  // old standing 'camera-viewshed' Fire-Risk toggle is REMOVED (PART IX:
  // contextual render beats a standing toggle). This pref is the per-operator
  // "hide viewshed" opt-out on the camera panel; honest default ON. A stale
  // stored `cameraViewshed` field is simply never read (graceful migration). ---
  /** Selecting a camera auto-drapes its terrain viewshed (default ON). */
  viewshedOnSelect: boolean;
  // --- Context furniture ---
  /** Provincial boundary polylines (Context group). Always-on base-map furniture
   *  by default, but operator-toggleable per the L.1 doctrine. */
  provincialBoundaries: boolean;
  // --- Briefing-Up view mode (B.1, operator-cop-doctrine §3B.7/§6.3) ---
  /** true = the elected-official Briefing-Up render profile; false = Operator COP.
   *  NOT a map layer — persisted via the same operator_layer_preferences store so
   *  the mode choice survives a reload + syncs cross-device. */
  briefingMode: boolean;
}

/** The aircraft_role sub-toggles, in display order. Colours come from the
 *  context-locked ./styles/aircraftRoles palette (rendered as the legend swatch
 *  in LayerToggle). */
export const AIRCRAFT_ROLES: ReadonlyArray<{ role: string; label: string }> = [
  { role: 'water_bomber', label: 'Water bombers' },
  { role: 'sar', label: 'SAR' },
  { role: 'air_ambulance', label: 'Air ambulance' },
  { role: 'police', label: 'Police' },
  { role: 'isr', label: 'ISR' },
  { role: 'multi_mission', label: 'Multi-mission' },
  // R.1e roster roles (Coast Guard helicopters + Op LENTUS airlift).
  { role: 'ccg_helo', label: 'Coast Guard helicopters' },
  { role: 'lentus_airlift', label: 'LENTUS airlift' },
  { role: 'sar_airlift', label: 'Strategic airlift' },
  { role: 'heavy_lift', label: 'Heavy lift' },
];

const KEY = 'tsr.layers';

/**
 * L.0 default toggle state. Every boolean field DERIVES from `defaultLayerOn`
 * (keyed by the field's server layer_key) so the default view cannot drift —
 * Hot Spots (`raw-hotspots`) is absent from DEFAULT_VIEW_LAYERS, so it resolves
 * to OFF. Array fields carry their empty default.
 */
const DEFAULTS: LayerPrefs = {
  cameras: defaultLayerOn('live-video-feeds'),
  sensors: defaultLayerOn('weather-stations'),
  smokePriorityOnly: defaultLayerOn('cameras-smoke-priority'),
  fireStations: defaultLayerOn('fire-stations'),
  wildfireBases: defaultLayerOn('wildfire-bases'),
  aviationBases: defaultLayerOn('sar-aviation'),
  ontarioCorps: defaultLayerOn('ontario-corps'),
  hiddenPartners: [],
  expandedCategories: [],
  liveAircraft: defaultLayerOn('aircraft'),
  hiddenAircraftRoles: [],
  reportedOcWildfires: defaultLayerOn('reported-oc-wildfires'),
  allReportedWildfires: defaultLayerOn('all-reported-wildfires'),
  fwiHeatmap: defaultLayerOn('fire-weather-index'),
  rawHotspots: defaultLayerOn('raw-hotspots'),
  fusionCandidates: defaultLayerOn('fusion-candidates'),
  cameraSmoke: defaultLayerOn('camera-smoke-detections'),
  urgentAnomalies: defaultLayerOn('urgent-anomalies'),
  extremeWeatherWarnings: defaultLayerOn('extreme-weather-warnings'),
  evacuationOrders: defaultLayerOn('bc-evacuation-orders'),
  operatorEvacuationOrders: defaultLayerOn('operator-evacuation-orders'),
  fireBans: defaultLayerOn('ab-fire-bans'),
  droughtConditions: defaultLayerOn('drought-conditions'),
  boilWaterAdvisories: defaultLayerOn('boil-water-advisories'),
  stateOfLocalEmergency: defaultLayerOn('bc-state-of-local-emergency'),
  tropicalCyclones: defaultLayerOn('tropical-cyclones'),
  ecccHurricaneTrack: defaultLayerOn('eccc-hurricane-track'),
  nrcanFloodExtent: defaultLayerOn('nrcan-flood-extent'),
  droneCoverage: defaultLayerOn('drone-operator-coverage'),
  stagingAreas: defaultLayerOn('staging-areas'),
  sectors: defaultLayerOn('sectors'),
  areaCommand: defaultLayerOn('area-command'),
  floodVulnerableCommunities: defaultLayerOn('flood-vulnerable-communities'),
  iceStormVulnerableCommunities: defaultLayerOn('ice-storm-vulnerable-communities'),
  communityVulnerabilityShading: defaultLayerOn('community-vulnerability-shading'),
  powerOutages: defaultLayerOn('ice-storm-power-outages'),
  roadClosures: defaultLayerOn('ice-storm-road-closures'),
  roadClosuresRestrictions: defaultLayerOn('ice-storm-road-closures-restrictions'),
  commsDegradationZones: defaultLayerOn('ice-storm-comms-degradation'),
  hydrometricGauges: defaultLayerOn('hydrometric-gauges'),
  floodInundation: defaultLayerOn('flood-inundation'),
  viewshedOnSelect: defaultLayerOn('viewshed-on-select'),
  provincialBoundaries: defaultLayerOn('provincial-boundaries'),
  briefingMode: defaultLayerOn('briefing-mode'),
};

/**
 * A fresh copy of the locked default prefs (#202 baseline). The seat-profile
 * apply path (#187) builds on this — applying a profile = these defaults + the
 * profile's layer DELTAS — so a profile can never fork the default-view lock.
 */
export function defaultLayerPrefs(): LayerPrefs {
  return { ...DEFAULTS };
}

const bool = (v: unknown, d: boolean): boolean => (typeof v === 'boolean' ? v : d);
const strArr = (v: unknown, d: string[]): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : d;

export function loadLayerPrefs(): LayerPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw) as Partial<LayerPrefs> & { fireRisk?: boolean };
    return {
      cameras: bool(p.cameras, DEFAULTS.cameras),
      sensors: bool(p.sensors, DEFAULTS.sensors),
      smokePriorityOnly: bool(p.smokePriorityOnly, DEFAULTS.smokePriorityOnly),
      fireStations: bool(p.fireStations, DEFAULTS.fireStations),
      wildfireBases: bool(p.wildfireBases, DEFAULTS.wildfireBases),
      aviationBases: bool(p.aviationBases, DEFAULTS.aviationBases),
      ontarioCorps: bool(p.ontarioCorps, DEFAULTS.ontarioCorps),
      hiddenPartners: strArr(p.hiddenPartners, DEFAULTS.hiddenPartners),
      expandedCategories: strArr(p.expandedCategories, DEFAULTS.expandedCategories),
      liveAircraft: bool(p.liveAircraft, DEFAULTS.liveAircraft),
      hiddenAircraftRoles: strArr(p.hiddenAircraftRoles, DEFAULTS.hiddenAircraftRoles),
      // Back-compat: pre-L.1 stored the single `fireRisk` flag; map it onto the
      // OC layer (the L.0 baseline it used to represent).
      reportedOcWildfires: bool(
        p.reportedOcWildfires,
        bool(p.fireRisk, DEFAULTS.reportedOcWildfires),
      ),
      allReportedWildfires: bool(p.allReportedWildfires, DEFAULTS.allReportedWildfires),
      fwiHeatmap: bool(p.fwiHeatmap, DEFAULTS.fwiHeatmap),
      rawHotspots: bool(p.rawHotspots, DEFAULTS.rawHotspots),
      fusionCandidates: bool(p.fusionCandidates, DEFAULTS.fusionCandidates),
      cameraSmoke: bool(p.cameraSmoke, DEFAULTS.cameraSmoke),
      urgentAnomalies: bool(p.urgentAnomalies, DEFAULTS.urgentAnomalies),
      extremeWeatherWarnings: bool(p.extremeWeatherWarnings, DEFAULTS.extremeWeatherWarnings),
      evacuationOrders: bool(p.evacuationOrders, DEFAULTS.evacuationOrders),
      operatorEvacuationOrders: bool(p.operatorEvacuationOrders, DEFAULTS.operatorEvacuationOrders),
      fireBans: bool(p.fireBans, DEFAULTS.fireBans),
      droughtConditions: bool(p.droughtConditions, DEFAULTS.droughtConditions),
      boilWaterAdvisories: bool(p.boilWaterAdvisories, DEFAULTS.boilWaterAdvisories),
      stateOfLocalEmergency: bool(p.stateOfLocalEmergency, DEFAULTS.stateOfLocalEmergency),
      tropicalCyclones: bool(p.tropicalCyclones, DEFAULTS.tropicalCyclones),
      ecccHurricaneTrack: bool(p.ecccHurricaneTrack, DEFAULTS.ecccHurricaneTrack),
      nrcanFloodExtent: bool(p.nrcanFloodExtent, DEFAULTS.nrcanFloodExtent),
      droneCoverage: bool(p.droneCoverage, DEFAULTS.droneCoverage),
      stagingAreas: bool(p.stagingAreas, DEFAULTS.stagingAreas),
      sectors: bool(p.sectors, DEFAULTS.sectors),
      areaCommand: bool(p.areaCommand, DEFAULTS.areaCommand),
      floodVulnerableCommunities: bool(
        p.floodVulnerableCommunities,
        DEFAULTS.floodVulnerableCommunities,
      ),
      iceStormVulnerableCommunities: bool(
        p.iceStormVulnerableCommunities,
        DEFAULTS.iceStormVulnerableCommunities,
      ),
      communityVulnerabilityShading: bool(
        p.communityVulnerabilityShading,
        DEFAULTS.communityVulnerabilityShading,
      ),
      powerOutages: bool(p.powerOutages, DEFAULTS.powerOutages),
      roadClosures: bool(p.roadClosures, DEFAULTS.roadClosures),
      roadClosuresRestrictions: bool(p.roadClosuresRestrictions, DEFAULTS.roadClosuresRestrictions),
      commsDegradationZones: bool(p.commsDegradationZones, DEFAULTS.commsDegradationZones),
      hydrometricGauges: bool(p.hydrometricGauges, DEFAULTS.hydrometricGauges),
      floodInundation: bool(p.floodInundation, DEFAULTS.floodInundation),
      // #262 — deliberately NOT seeded from the stale `cameraViewshed` field: the
      // old value meant "standing layer toggle" (default OFF), the new one means
      // "drape on select" (ruled default ON). Carrying `false` over would hide the
      // viewshed for every operator who never touched the old toggle.
      viewshedOnSelect: bool(p.viewshedOnSelect, DEFAULTS.viewshedOnSelect),
      provincialBoundaries: bool(p.provincialBoundaries, DEFAULTS.provincialBoundaries),
      briefingMode: bool(p.briefingMode, DEFAULTS.briefingMode),
    };
  } catch {
    return DEFAULTS;
  }
}

export function saveLayerPrefs(prefs: LayerPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** True when any response-asset sublayer is enabled (drives the asset fetch). */
export function anyAssetLayerOn(p: LayerPrefs): boolean {
  return p.fireStations || p.wildfireBases || p.aviationBases || p.ontarioCorps;
}

/** asset_class values to request from the API for the enabled sublayers. */
export function enabledAssetClasses(p: LayerPrefs): string[] {
  const classes: string[] = [];
  if (p.fireStations) classes.push('fire_station');
  if (p.wildfireBases) classes.push('wildfire_base');
  if (p.aviationBases) classes.push('aviation_base');
  if (p.ontarioCorps) classes.push('umbrella_org', 'illustrative_marker');
  return classes;
}

/**
 * The two wildfire-layer toggles the fetch filters derive from — a structural
 * subset of {@link LayerPrefs}, so the operator COP passes its full `prefs` while
 * the read-only wall (WallMap) passes its wall-lock-derived booleans through the
 * SAME single source (fixing the historical inline-`['OC']`/`['FUL']` drift that
 * silently undid #607's relaxation — nothing called these helpers).
 */
export interface WildfireLayerState {
  allReportedWildfires: boolean;
  reportedOcWildfires: boolean;
}

/**
 * Canonical stage filter for the ActiveFiresLayer (L.1.2 two-toggle). When the
 * Weather-group "All Reported Wildfires" layer is on, show every stage
 * (undefined = no filter); otherwise the L.0 "Reported OC Wildfires" layer
 * restricts to stage 'OC'. Returns null when neither is on (layer hidden).
 */
export function fireStageFilter(p: WildfireLayerState): string[] | undefined | null {
  if (p.allReportedWildfires) return undefined; // all stages
  if (p.reportedOcWildfires) return ['OC'];
  return null; // neither on → layer not shown
}

/**
 * Companion response_type filter (L.2.1). The default "Reported OC Wildfires" layer
 * fetches EVERY OC response type (undefined = no response_type narrowing) —
 * fix/wildfire-oc-render-lod relaxed the former ['FUL']-only narrowing so the honest
 * four OC classes (Full / Modified / Monitored / response-not-reported) all reach the
 * client and the LOD, not a fetch filter, governs visibility (urgent OC always visible;
 * non-urgent OC provincial-zoom only). The all-stages "Reported Wildfires" layer likewise
 * applies no response_type filter. null when neither wildfire layer is on (layer hidden).
 * (The stage filter still differs: OC-only ['OC'] vs all-stages undefined — see
 * fireStageFilter; only the response_type narrowing is dropped here.)
 *
 * ★ THE #607-DEATH GUARD: this MUST be the sole source the map fetch reads. App.tsx +
 * WallMap.tsx call it — an inline `['FUL']` re-narrows the default OC layer to
 * full-response only and Ontario's monitored fires (no CWFIS response class on the raw
 * provincial row) fall off the map via the API's `OR response_type IS NULL` un-gate while
 * still landing on the urgent queue (a red row with no icon). Pinned by defaultViewLock.test.ts.
 */
export function fireResponseTypeFilter(p: WildfireLayerState): string[] | undefined | null {
  if (p.allReportedWildfires) return undefined; // all response types
  if (p.reportedOcWildfires) return undefined; // ALL OC response types — LOD governs visibility
  return null;
}

/** True when either wildfire layer is on (drives ActiveFiresLayer visibility). */
export function anyWildfireLayerOn(p: WildfireLayerState): boolean {
  return p.reportedOcWildfires || p.allReportedWildfires;
}

/* ------------------------------------------------------------------ *
 * Server-side persistence (L.1.3) — maps LayerPrefs <-> server layer_keys.
 * ------------------------------------------------------------------ */

/** Maps each persisted server layer_key to its LayerPrefs boolean field. The
 *  set of keys here is exactly LAYER_PREFERENCE_DEFAULTS (the shared contract). */
export const LAYER_KEY_TO_PREF: Record<LayerKey, keyof LayerPrefs> = {
  'live-video-feeds': 'cameras',
  'cameras-smoke-priority': 'smokePriorityOnly',
  'urgent-anomalies': 'urgentAnomalies',
  aircraft: 'liveAircraft',
  'reported-oc-wildfires': 'reportedOcWildfires',
  'all-reported-wildfires': 'allReportedWildfires',
  'fire-weather-index': 'fwiHeatmap',
  'raw-hotspots': 'rawHotspots',
  'fusion-candidates': 'fusionCandidates',
  'camera-smoke-detections': 'cameraSmoke',
  'extreme-weather-warnings': 'extremeWeatherWarnings',
  'bc-evacuation-orders': 'evacuationOrders',
  'operator-evacuation-orders': 'operatorEvacuationOrders',
  'ab-fire-bans': 'fireBans',
  'drought-conditions': 'droughtConditions',
  'boil-water-advisories': 'boilWaterAdvisories',
  'bc-state-of-local-emergency': 'stateOfLocalEmergency',
  'tropical-cyclones': 'tropicalCyclones',
  'eccc-hurricane-track': 'ecccHurricaneTrack',
  'nrcan-flood-extent': 'nrcanFloodExtent',
  'sar-aviation': 'aviationBases',
  'fire-stations': 'fireStations',
  'wildfire-bases': 'wildfireBases',
  'ontario-corps': 'ontarioCorps',
  'drone-operator-coverage': 'droneCoverage',
  'staging-areas': 'stagingAreas',
  sectors: 'sectors',
  'area-command': 'areaCommand',
  'flood-vulnerable-communities': 'floodVulnerableCommunities',
  'ice-storm-vulnerable-communities': 'iceStormVulnerableCommunities',
  'community-vulnerability-shading': 'communityVulnerabilityShading',
  'ice-storm-power-outages': 'powerOutages',
  'ice-storm-road-closures': 'roadClosures',
  'ice-storm-road-closures-restrictions': 'roadClosuresRestrictions',
  'ice-storm-comms-degradation': 'commsDegradationZones',
  'hydrometric-gauges': 'hydrometricGauges',
  'flood-inundation': 'floodInundation',
  'viewshed-on-select': 'viewshedOnSelect',
  'weather-stations': 'sensors',
  'provincial-boundaries': 'provincialBoundaries',
  'briefing-mode': 'briefingMode',
};

/** Project the persisted boolean prefs into a server layer_key map. */
export function prefsToLayerMap(p: LayerPrefs): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const key of LAYER_KEYS) {
    out[key] = Boolean(p[LAYER_KEY_TO_PREF[key]]);
  }
  return out;
}

/** Merge a server layer_key map onto local prefs (server wins for known keys). */
export function applyServerLayerMap(
  base: LayerPrefs,
  serverMap: Record<string, boolean>,
): LayerPrefs {
  const next = { ...base };
  for (const key of LAYER_KEYS) {
    const v = serverMap[key];
    if (typeof v === 'boolean') {
      (next[LAYER_KEY_TO_PREF[key]] as boolean) = v;
    }
  }
  return next;
}

/** The server's layer-preference response: the resolved toggle map + whether the
 *  operator has EVER persisted a preference row (#458 first-run signal). */
export interface ServerLayerPrefs {
  preferences: Record<string, boolean>;
  /** true ⇒ a returning operator (their stored prefs win on boot); false ⇒ a fresh
   *  operator the role→seat boot scaffold may seed. */
  hasStored: boolean;
}

/**
 * GET the operator's server-side layer preferences. Non-blocking by design — the
 * caller renders from its localStorage cache first and merges this in when it
 * arrives. Returns null on any error (offline / 5xx) so the caller falls back to
 * local state with no UI disruption (L.1.5 #1, #2).
 */
export async function fetchServerLayerPrefs(
  signal?: AbortSignal,
): Promise<ServerLayerPrefs | null> {
  try {
    const res = await fetch('/api/operator-preferences/layers', { signal });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      preferences?: Record<string, boolean>;
      hasStored?: boolean;
    };
    if (!body.preferences) return null;
    // Absent hasStored (an older server) → treat as a returning operator so a boot
    // can NEVER reseed/clobber stored prefs it can't see; only an explicit false
    // (the current server, no rows) opts into the first-run seed.
    return { preferences: body.preferences, hasStored: body.hasStored ?? true };
  } catch {
    return null;
  }
}

// --- Debounced PUT batching (L.1.5 #3) -------------------------------------
let pendingUpdates: Record<string, boolean> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const PUT_DEBOUNCE_MS = 500;

async function flushLayerPrefs(): Promise<void> {
  flushTimer = null;
  const preferences = pendingUpdates;
  pendingUpdates = {};
  if (Object.keys(preferences).length === 0) return;
  try {
    await fetch('/api/operator-preferences/layers', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferences }),
    });
  } catch {
    // Server unreachable → localStorage remains the source of truth; the next
    // toggle re-attempts. We intentionally do NOT requeue (avoids an unbounded
    // retry storm); the full state re-syncs on the next successful toggle.
  }
}

/**
 * Queue a server PUT for the changed layer keys, debounced 500ms so a rapid
 * burst of toggles collapses into one request. Accepts the full prefs object and
 * the previous prefs; only the keys whose value actually changed are queued.
 */
export function queueServerLayerSync(next: LayerPrefs, prev: LayerPrefs): void {
  if (typeof window === 'undefined') return;
  const nextMap = prefsToLayerMap(next);
  const prevMap = prefsToLayerMap(prev);
  let changed = false;
  for (const key of LAYER_KEYS) {
    if (nextMap[key] !== prevMap[key]) {
      pendingUpdates[key] = nextMap[key]!;
      changed = true;
    }
  }
  if (!changed) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => void flushLayerPrefs(), PUT_DEBOUNCE_MS);
}

/** Test seam: synchronously flush any pending server sync. */
export async function __flushServerLayerSyncNow(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  await flushLayerPrefs();
}
